import FieldList from './FieldList';
import SelectOptions from '../Services/SelectOptions';
import { SCORE_OPTIONS_AVERAGE, COLUMNS_OPTIONS } from '../Context/DynamicFormContext';
import Utils from '../Services/Utils';
import FieldCondition from "./FieldCondition";
import Attachments from "../Services/api/Attachments";

export const fontSizeOptions = () => {
  const arr = [];
  for (let i = 14; i <= 50; i += 2) {
    arr.push({
      value: i, label: i + 'px',
    });
  }
  return arr;
};

class Section {
  constructor(item = {}) {
    const cloneEnabled = !Utils.isNull(item.clone_enabled) ? item.clone_enabled : item.cloneEnabled;
    const scoreType = !Utils.isNull(item.score_type) ? item.score_type : item.scoreType;
    const fontSize = !Utils.isNull(item.font_size) ? item.font_size : item.fontSize;
    const tags_available = !Utils.isNull(item.tags_available) ?
    item.tags_available.map(tag => !Utils.isNull(tag.value) ? tag.value : tag) : null;
    this.id = item.id || undefined;
    this.index = item.index || 0;
    this.columns = undefined;
    if (item.columns) {
      this.columns = (!item.columns.value ? SelectOptions.getValue(COLUMNS_OPTIONS, item.columns) : item.columns);
    }
    this.bold = item.bold || undefined;
    this.label = item.label || undefined;
    this.hide = item.hide || undefined;
    this.cloneEnabled = cloneEnabled || undefined;
    this.component = item.component || 'sectionDivider';
    this.italic = item.italic || undefined;
    this.fontSize = undefined;
    if (fontSize) {
      this.fontSize = (!fontSize.value ? SelectOptions.getValue(fontSizeOptions(), fontSize.split('px').join('')) : fontSize);
    }
    if (!Utils.isNull(scoreType)) {
      this.scoreType = (Utils.isNull(scoreType.value) ? SelectOptions.getValue(SCORE_OPTIONS_AVERAGE, scoreType) : scoreType);
    }
    else {
      this.scoreType = { value: 0, label: 'No score' };
    }
    this.fields = item.fields || new FieldList();
    this.disabled = !Utils.isNull(item.disabled) ? item.disabled : undefined;
    this.originId = item.originId || undefined;
    this.lockDDR = item.lockDDR || undefined;
    this.lockLabel = item.lockLabel || undefined;
    this.conditions = item.conditions || [];
    this.errors = {};
    this.tags_available = tags_available;
  }

  static parse = async (item, fieldsIdLabelDict, fieldsIdOptionsDict, disabled, chargeCodes) => {
    const cloneEnabled = !Utils.isNull(item.clone_enabled) ? item.clone_enabled : item.cloneEnabled;
    const lockDDR = !Utils.isNull(item.lock_ddr) ? item.lock_ddr : item.lockDDR;
    const lockLabel = !Utils.isNull(item.lock_label) ? item.lock_label : item.lockLabel;
    const scoreType = !Utils.isNull(item.score_type) ? item.score_type : item.scoreType;
    const fontSize = !Utils.isNull(item.font_size) ? item.font_size : item.fontSize;
    const tags_available = !Utils.isNull(item.tags_available) ?
    item.tags_available.map(tag => !Utils.isNull(tag.value) ? tag.value : tag) : null;
    const conditionList = (item.conditions || []).map(
      (condition) => FieldCondition.parse(condition, fieldsIdLabelDict, fieldsIdOptionsDict));
    const fieldsItems = !Utils.isNull(item.children) ? item.children : item.fields;
    let columnsItems = undefined;
    if (item.columns) {
      columnsItems = (!item.columns.value ? SelectOptions.getValue(COLUMNS_OPTIONS, item.columns) : item.columns);
    }
    let fontSizeValue = undefined;
    if (fontSize) {
      fontSizeValue = (!fontSize.value ? SelectOptions.getValue(fontSizeOptions(), fontSize.split('px').join('')) : fontSize);
    }
    let scoreTypeValue = null;
    if (!Utils.isNull(scoreType)) {
      scoreTypeValue = (Utils.isNull(scoreType.value) ? SelectOptions.getValue(SCORE_OPTIONS_AVERAGE, scoreType) : scoreType);
    }
    else {
      scoreTypeValue = { value: 0, label: 'No score' };
    }
    return new Section({
      id: item.id || undefined,
      index: item.index || 0,
      columns: columnsItems,
      bold: item.bold || undefined,
      label: item.label || undefined,
      hide: item.hide || undefined,
      cloneEnabled: cloneEnabled || undefined,
      component: item.component || 'sectionDivider',
      italic: item.italic || undefined,
      fontSize: fontSizeValue,
      scoreType: scoreTypeValue,
      fields: fieldsItems ?
        await FieldList.parse(item.id, fieldsItems, fieldsIdLabelDict, fieldsIdOptionsDict, disabled, chargeCodes) : new FieldList(),
      disabled: !Utils.isNull(item.disabled) ? item.disabled : undefined,
      originId: item.originId || null,
      lockDDR: lockDDR || null,
      lockLabel: lockLabel || null,
      conditions: conditionList,
      tags_available: tags_available,
   });
  };

  serialize = (builder) => {
    const conditions = (this.conditions || []).map(item => item.serialize());
    return {
      id: this.id,
      index: this.index,
      columns: this.columns ? this.columns.value : undefined,
      bold: this.bold,
      label: this.label,
      hide: this.hide,
      clone_enabled: this.cloneEnabled,
      component: this.component,
      italic: this.italic,
      font_size: this.fontSize ? this.fontSize.label : undefined,
      score_type: !Utils.isNull(this.scoreType) && !Utils.isNull(this.scoreType.value) ? this.scoreType.value : this.scoreType,
      children: this.fields.serialize(this.label, builder),
      originId: builder ? undefined : this.originId || undefined,
      lock_ddr: this.lockDDR || undefined,
      lock_label: this.lockLabel || undefined,
      conditions: conditions,
      tags_available: this.tags_available || [],
    };
  };

  serializeAsync = async (builder) => {
    const conditions = (this.conditions || []).map(item => item.serialize());
    return {
      id: this.id,
      index: this.index,
      columns: this.columns ? this.columns.value : undefined,
      bold: this.bold,
      label: this.label,
      hide: this.hide,
      clone_enabled: this.cloneEnabled,
      component: this.component,
      italic: this.italic,
      font_size: this.fontSize ? this.fontSize.label : undefined,
      score_type: !Utils.isNull(this.scoreType) && !Utils.isNull(this.scoreType.value) ? this.scoreType.value : this.scoreType,
      children: await this.fields.serializeAsync(this.label, builder),
      originId: builder ? undefined : this.originId || undefined,
      lock_ddr: this.lockDDR || undefined,
      lock_label: this.lockLabel || undefined,
      conditions: conditions,
      tags_available: this.tags_available || [],
    };
  };

  changeValue(field, value) {
    this[field] = value;
  }

  changeValues(values) {
    for (const field in values) {
      this[field] = values[field];
    }
    return values;
  }
}

export default Section;